import {
	auditService,
	casesLawyerService,
	GET_CASE_STEPS_STATUS,
	GET_LAWYER_CASE_DETAILS,
} from '@/api';
import { Button, Icon, Typography } from '@/components/common';
import { InputDate, InputList, InputText } from '@/components/form';
import {
	UploadFileCard,
	UploadPDFModal,
} from '@/components/modals/UploadPDFModal';
import Icons from '@/const/icons';
import { useNotification, usePhaseStatus } from '@/hooks';
import {
	CaseDetailsLawyer,
	CaseSettlementDetails,
	HttpResponse,
	ApprovePhase,
	SaveCaseSettlement,
	StepStatusCase,
	StepStatusCaseData,
	StepStatusCaseType,
	SettlementType,
} from '@/interfaces';
import { formatCurrencyAlt } from '@/utils';
import {
	QueryObserverResult,
	RefetchOptions,
	useQueryClient,
} from '@tanstack/react-query';
import clsx from 'clsx';
import React from 'react';
import { FieldValues, useForm, useWatch } from 'react-hook-form';
import { CaseContext } from '@/context/CaseContext';
import { useDebouncedCallback } from 'use-debounce';
import { format, parseISO } from 'date-fns';

interface Props {
	caseDetails: CaseDetailsLawyer | undefined;
	caseSteps: StepStatusCaseData | undefined;
	stepCase?: StepStatusCase;
	statusStep: StepStatusCaseType;
	settlementDetail: HttpResponse<CaseSettlementDetails> | undefined;
	refetchDetails: (
		options?: RefetchOptions | undefined
	) => Promise<QueryObserverResult<HttpResponse<CaseSettlementDetails>, Error>>;
}

const cleanFormatAmount = (amount: string) => {
	if (!amount) return 0;
	return Number(amount.replace(/[^0-9.]/g, ''));
};

const typeOfSettlementOptions = [
	{
		name: '',
		label: 'Select',
		placeholder: true,
	},
	{
		name: 'No_Signature_Required',
		label: 'No Signature Required',
	},
	{
		name: 'Signature_Required',
		label: 'Signature Required',
	},
];

export const SettlementDetailsForm: React.FC<Props> = ({
	caseDetails,
	// stepCase,
	// statusStep,
	settlementDetail,
	caseSteps,
	refetchDetails,
}) => {
	const rules = {
		totalAmount: {
			required: { value: true, message: 'This is required.' },
		},
		attorneyFees: {
			required: { value: true, message: 'This is required.' },
		},
		date: {
			required: { value: true, message: 'This is required.' },
		},
		attorneyExpenses: {
			required: { value: true, message: 'This is required.' },
		},
	};

	const lastSavedData = React.useRef<CaseSettlementDetails>();

	const { isCaseReadOnly, setSaveStatus } = React.useContext(CaseContext);
	const { phaseId, stepId } = usePhaseStatus({
		caseSteps,
		phaseName: 'Settlement Phase',
		stepName: 'Settlement Details',
	});
	const {
		watch,
		reset,
		register,
		setValue,
		getValues,
		control,
		clearErrors,
		formState: { errors },
	} = useForm();
	const queryClient = useQueryClient();
	const { toastSuccess, toastError } = useNotification();

	// this is used to save the pdf file in the modal (TEMPORARY)
	const [pdfFileUploaded, setPdfFileUploaded] = React.useState<
		File | undefined
	>();
	// this is used to save the pdf file in the form (PERMANENT)
	const [pdfFile, setPdfFile] = React.useState<File | undefined>();
	const [pdfName, setPdfName] = React.useState<string>('');
	const [fileError, setFileError] = React.useState<boolean>(false);
	const [isUploadDocumentModalOpen, setIsUploadDocumentModalOpen] =
		React.useState<boolean>(false);
	const [signatureRequiredValue, setSignatureRequiredValue] =
		React.useState<SettlementType | null>(null);
	const [signatureRequiredError, setSignatureRequiredError] =
		React.useState<boolean>(false);

	React.useEffect(() => {
		if (settlementDetail?.data) {
			const values = settlementDetail?.data;
			reset({
				totalAmount:
					values.total_amount !== 0
						? formatCurrencyAlt(String(values.total_amount), 'blur')
						: '',
				date: values.date_of_settlement,
				attorneyFees:
					values.attorney_fees !== 0
						? formatCurrencyAlt(String(values.attorney_fees), 'blur')
						: '',
				attorneyExpenses:
					values.attorney_expenses !== 0
						? formatCurrencyAlt(String(values.attorney_expenses), 'blur')
						: '',
				typeOfSettlement: values.type_of_settlement,
				file: values.settlement_agreement,
			});

			if (values.total_amount) {
				setSignatureRequiredValue(values.type_of_settlement);
			}
			// const settlementUrl = settlementDetail.data.settlement_agreement;
			// const fileName = settlementUrl ? settlementUrl.split('/').at(-1) : '';
			const fileName = settlementDetail.data.settlement_agreement
				? 'Settlement Agreement.pdf'
				: '';
			setPdfName(fileName);
			lastSavedData.current = values;
			setPdfFile(undefined); // reset pdf file
			// setPdfFileUploaded(undefined); // reset pdf file uploaded
		}
	}, [settlementDetail]);

	const formValues = useWatch({
		control,
		defaultValue: getValues(),
	});

	const getInfoIsEdited = () => {
		const currentValues = getValues();

		const totalAmount = cleanFormatAmount(currentValues.totalAmount);
		const attorneyFees = cleanFormatAmount(currentValues.attorneyFees);
		const attorneyExpenses = cleanFormatAmount(currentValues.attorneyExpenses);

		return (
			lastSavedData.current?.total_amount !== totalAmount ||
			lastSavedData.current?.attorney_fees !== attorneyFees ||
			lastSavedData.current?.attorney_expenses !== attorneyExpenses ||
			lastSavedData.current?.date_of_settlement !== currentValues.date ||
			lastSavedData.current?.type_of_settlement !==
				currentValues.typeOfSettlement ||
			pdfFile !== undefined
		);
	};

	const debouncedFunction = useDebouncedCallback(
		(type: 'save' | 'complete') => {
			if (type === 'complete') {
				handleComplete(getValues());
			} else {
				handleSave();
			}
		},
		1000
	);

	// Separate function to check if filled with current data
	const checkIsFilled = React.useCallback(
		(currentValues: FieldValues) => {
			const totalAmount = cleanFormatAmount(currentValues.totalAmount);
			const attorneyFees = cleanFormatAmount(currentValues.attorneyFees);
			const attorneyExpenses = cleanFormatAmount(
				currentValues.attorneyExpenses
			);

			return (
				totalAmount !== 0 &&
				attorneyFees !== 0 &&
				attorneyExpenses !== 0 &&
				!!currentValues.date &&
				!!signatureRequiredValue &&
				(!!currentValues.file || !!pdfFile)
			);
		},
		[signatureRequiredValue, pdfFile]
	);

	const isFormFilled = React.useMemo(() => {
		return checkIsFilled(formValues);
	}, [formValues, checkIsFilled]);

	React.useEffect(() => {
		if (!settlementDetail?.data) return;

		clearErrors();
		if (getInfoIsEdited()) {
			const currentValues = getValues();
			const currentlyFilled = checkIsFilled(currentValues);

			if (currentlyFilled) {
				debouncedFunction('complete');
			} else {
				debouncedFunction('save');
			}
		}
	}, [formValues, settlementDetail, signatureRequiredValue, pdfFile]);

	React.useEffect(() => {
		if (pdfFile) {
			setFileError(false);
		}
		if (signatureRequiredValue) {
			setSignatureRequiredError(false);
		}
	}, [pdfFile, signatureRequiredValue]);

	const refetchQueries = async () => {
		refetchDetails();
		const caseId = caseDetails?.id;
		if (caseId) {
			queryClient.refetchQueries({
				queryKey: [GET_CASE_STEPS_STATUS, caseId],
			});
			queryClient.refetchQueries({
				queryKey: [GET_LAWYER_CASE_DETAILS, caseId],
			});
		}
	};

	const getIsError = () => {
		if (
			!pdfFile &&
			!pdfName &&
			signatureRequiredValue === 'Signature_Required'
		) {
			setFileError(true);
			return true;
		}

		if (!signatureRequiredValue) {
			setSignatureRequiredError(true);

			return true;
		}

		setFileError(false);
		return false;
	};

	const getData = (values: FieldValues): SaveCaseSettlement => {
		return {
			total_amount: cleanFormatAmount(values.totalAmount),
			attorney_fees: cleanFormatAmount(values.attorneyFees),
			type_of_settlement: values.typeOfSettlement,
			date_of_settlement: values.date,
			attorney_expenses: cleanFormatAmount(values.attorneyExpenses),
			file: pdfFile,
		};
	};

	const handleSendSettlementAgreement = async () => {
		try {
			await casesLawyerService.confirmSettlementAgreement(
				caseDetails?.id as string
			);
			await handleApprove();
			toastSuccess('The settlement agreement has been successfully sent.');
		} catch (error) {
			toastError('An error has occurred. Please try again.');
		}
	};

	const handleComplete = async (values: FieldValues) => {
		const isError = getIsError();
		if (isError) return;
		const request = getData(values);
		try {
			await casesLawyerService.caseSettlementComplete(
				caseDetails?.id as string,
				request
			);
			// toastSuccess('The case section has been successfully saved.');
			refetchQueries();
			// setPdfFile(undefined);
			// reset(getValues());
		} catch (error) {
			toastError(
				'An error occurred while confirming completion. Please try again.'
			);
		}
	};

	const handleSave = async () => {
		const values = getValues();
		const request = getData(values);
		setSaveStatus({ isSaving: true, isError: false });
		try {
			await casesLawyerService.caseSettlementSave(
				caseDetails?.id as string,
				request
			);
			refetchQueries();
			// setPdfFile(undefined);
			// reset(getValues());
		} catch (error) {
			setSaveStatus({ isError: true });
			toastError(
				'An error occurred while saving the case section. Please try again.'
			);
		} finally {
			setSaveStatus({ isSaving: false, isError: false });
		}
	};

	const handleApprove = async () => {
		try {
			if (caseDetails?.id && phaseId && stepId) {
				const data: ApprovePhase = {
					case_id: caseDetails?.id as string,
					lawyer_stepper_id: phaseId,
					stepper_form_id: stepId,
				};

				await auditService.postApprovePhase(data);
				// toastSuccess('The case section has been successfully approved.');
				refetchQueries();
				// setPdfFile(undefined);
			}
		} catch (error) {
			toastError(
				'An error occurred while confirming approval. Please try again.'
			);
		}
	};

	const handleChange = (event: any, name: string) => {
		const rawValue = event.target.value;
		const formatted = formatCurrencyAlt(rawValue, handleBlur);
		setValue(name, formatted, { shouldDirty: true });
	};

	const handleBlur = (name: string) => {
		const amount = getValues(name);
		const formattedAmount = formatCurrencyAlt(amount, 'blur');
		setValue(name, formattedAmount);
	};

	const currentTotalAmount = watch('totalAmount');
	const currentAttorneyFees = watch('attorneyFees');
	const currentAttorneyExpenses = watch('attorneyExpenses');
	// const currentDate = watch('date');

	// const currentSignature = signatureRequiredValue;
	// const isGeneralDisabled = isCaseReadOnly;
	// const isDefault = statusStep === 'default';
	// const isIncomplete = statusStep === 'incomplete';
	// const isCompleted = statusStep === 'completed';
	// const isApproved = statusStep === 'approved';

	// const isTypeOffSettlementChanged =
	// 	currentSignature === settlementDetail?.data.type_of_settlement;
	// const isFileChanged =
	// 	(!settlementDetail?.data.settlement_agreement && pdfFile) ||
	// 	(settlementDetail?.data.settlement_agreement && pdfFile) ||
	// 	(settlementDetail?.data.settlement_agreement && !pdfName);

	// const isValid =
	// 	!!currentTotalAmount &&
	// 	!!currentAttorneyFees &&
	// 	!!currentAttorneyExpenses &&
	// 	!!currentSignature &&
	// 	!!currentDate &&
	// 	(!!pdfFile || !!pdfName);

	// const isSaveEnable =
	// 	(!isGeneralDisabled && !!isDirty) ||
	// 	(signatureRequiredValue &&
	// 		currentSignature !== settlementDetail?.data.type_of_settlement) ||
	// 	isFileChanged;

	// const isCompleteEnable =
	// 	(!isGeneralDisabled && (isDefault || isIncomplete) && isValid) ||
	// 	((isApproved || isCompleted) && isSaveEnable && isValid);

	// const isPreviewEnabled = (isCompleted || isApproved) && !isSaveEnable;

	// const isCompletedDisable =
	// 	isGeneralDisabled ||
	// 	((isApproved || isCompleted) &&
	// 		!isDirty &&
	// 		isTypeOffSettlementChanged &&
	// 		!isFileChanged);

	// const settlementFileUrl = settlementDetail?.data.settlement_agreement;
	// const settlementFileName = settlementFileUrl?.split('/').at(-1) ?? '';

	// const handleDownload = async () => {
	// 	if (pdfFile) {
	// 		await downloadBlobPDF(pdfFile);
	// 		return;
	// 	}
	// 	if (settlementFileUrl) {
	// 		await downloadPDFByUrl(settlementFileUrl, settlementFileName);
	// 	}
	// };
	return (
		<>
			<form>
				<div className="flex flex-col gap-6 mb-6">
					<div className="flex flex-col lg:flex-row  gap-6 w-full">
						<InputText
							disabled={isCaseReadOnly}
							register={register}
							name="totalAmount"
							title={'Total Amount'}
							className="w-full"
							iconLeft={
								!currentTotalAmount || isCaseReadOnly
									? Icons.currency
									: Icons.currencyBlack
							}
							classNameIcon="!h-4 !w-4 mt-1 !text-primary"
							classNameInput="!pl-9 "
							onChange={(e) => handleChange(e, 'totalAmount')}
							onBlur={() => handleBlur('totalAmount')}
							rules={rules.totalAmount}
							error={errors.totalAmount}
						/>
						{/* This extra div was add because the input date was not taking the full width if it was not inside a div with flex-col */}
						<div className="flex flex-col w-full">
							<InputDate
								disabled={isCaseReadOnly}
								register={register}
								title="Date of Settlement"
								setValueInput={(name: string, data: any) => {
									if (data) {
										setValue(name, data, { shouldDirty: true });
									}
								}}
								name="date"
								classNameInput="!text-black"
								rules={rules.date}
								error={errors.date}
							/>
						</div>
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
						<InputText
							disabled={isCaseReadOnly}
							register={register}
							name="attorneyFees"
							title={'Attorney Fees'}
							className="w-full"
							iconLeft={
								!currentAttorneyFees || isCaseReadOnly
									? Icons.currency
									: Icons.currencyBlack
							}
							classNameIcon={'!h-4 !w-4 mt-1 !text-primary'}
							classNameInput="!pl-9"
							onChange={(e) => handleChange(e, 'attorneyFees')}
							onBlur={() => handleBlur('attorneyFees')}
							rules={rules.attorneyFees}
							error={errors.attorneyFees}
						/>
						<InputText
							disabled={isCaseReadOnly}
							register={register}
							name="attorneyExpenses"
							title={'Attorney Expenses'}
							className="w-full"
							iconLeft={
								!currentAttorneyExpenses || isCaseReadOnly
									? Icons.currency
									: Icons.currencyBlack
							}
							onChange={(e) => handleChange(e, 'attorneyExpenses')}
							onBlur={() => handleBlur('attorneyExpenses')}
							rules={rules.attorneyExpenses}
							error={errors.attorneyExpenses}
							classNameIcon="!h-4 !w-4 mt-1 !text-primary"
							classNameInput="!pl-9"
						/>
						<div className="w-full">
							<Typography type="regular-16" className="mb-2">
								Type of Settlement
							</Typography>
							{settlementDetail && (
								<InputList
									name="typeOfSettlement"
									register={register}
									setValueInput={setValue}
									className="w-full"
									options={typeOfSettlementOptions}
									myDefaultValue={
										settlementDetail?.data.type_of_settlement ?? undefined
									}
									disabled={isCaseReadOnly}
								/>
							)}
							<div
								className={clsx('flex items-start gap-2 mt-2', {
									hidden: !signatureRequiredError,
								})}
							>
								<Icon
									src={Icons.infoCircle}
									className="!size-5 text-main-red"
								/>
								<Typography type="regular-12" className="text-main-red mt-1">
									Please fill out the required field.
								</Typography>
							</div>
						</div>
					</div>
					<div className="flex flex-col gap-6 w-full">
						<div className="w-full">
							<Typography type="semibold-16" className="mb-6">
								Settlement Agreement
							</Typography>

							{!pdfFile && !pdfName && (
								<Button
									iconLeft
									size="medium"
									label="Upload Document"
									icon={Icons.documentUpload}
									className="!py-1 !px-[35px]  !border-green-base mb-2"
									classNameButton="!w-4 !h-4"
									disabled={isCaseReadOnly}
									onClick={() => {
										setPdfFileUploaded(undefined); // reset pdf file uploaded
										setIsUploadDocumentModalOpen(true);
									}}
								/>
							)}
							{(pdfFile?.name || pdfName) && (
								<div className="flex gap-2 items-center">
									{(pdfFile?.name || pdfName) && (
										<UploadFileCard
											pdfName={pdfFile?.name ?? pdfName}
											setPdfFile={setPdfFile}
											removeIconRemove={Icons.trash}
											classNameIcon="text-main-red"
											handleRemove={() => {
												setPdfFile(undefined);
												setPdfName('');
											}}
											disabled={
												isCaseReadOnly ||
												!!settlementDetail?.data?.date_settlement_sent
											}
										/>
									)}
									{settlementDetail?.data?.date_settlement_sent ? (
										<Typography
											type="regular-10"
											className="text-opacity-black leading-3"
										>
											Sent <br />
											{format(
												parseISO(settlementDetail?.data?.date_settlement_sent),
												'MMM dd, yyyy'
											)}
										</Typography>
									) : (
										<Button
											label="Send"
											decoration="primary"
											size="fit"
											className="h-12"
											onClick={handleSendSettlementAgreement}
											disabled={isCaseReadOnly || !isFormFilled}
										/>
									)}
								</div>
							)}
							{fileError && (
								<div className="flex gap-2">
									<Icon
										src={Icons.exclamationCircle}
										width={16}
										height={16}
										className="!text-main-red"
									/>
									<Typography type="regular-12" className="text-main-red">
										Please upload the required file.
									</Typography>
								</div>
							)}
						</div>
					</div>
				</div>
			</form>
			<UploadPDFModal
				isOpen={isUploadDocumentModalOpen}
				setIsModalOpen={setIsUploadDocumentModalOpen}
				setPdfFile={setPdfFileUploaded}
				pdfFile={pdfFileUploaded}
				handleConfirmButton={() => {
					setIsUploadDocumentModalOpen(false);
					setPdfFile(pdfFileUploaded);
				}}
			/>
		</>
	);
};
